import { SvgSubElement } from '../types/svgTypes';

/**
 * 为 SVG 内容中的元素生成唯一 ID
 */
const generateSubElementId = (): string => {
  return `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 可编辑的 SVG 标签列表
 */
const EDITABLE_TAGS = ['path', 'circle', 'rect', 'line', 'ellipse', 'polygon', 'polyline', 'g', 'text'];

/**
 * 应该跳过的标签（定义类、动画类）
 */
const SKIP_TAGS = ['defs', 'filter', 'clippath', 'mask', 'symbol', 'style', 'animate', 'animatetransform', 'animatemotion', 'set', 'lineargradient', 'radialgradient', 'stop', 'femerge', 'femergenode', 'fegaussianblur'];

/**
 * 检查颜色值是否为渐变引用
 */
const isGradientReference = (color: string | null | undefined): boolean => {
  if (!color) return false;
  return color.trim().startsWith('url(');
};

/**
 * 从 text 元素中提取文本内容（包括嵌套的 tspan）
 */
const extractTextContent = (element: Element): string => {
  // 获取直接文本节点
  let text = '';
  
  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const child = node as Element;
      if (child.tagName.toLowerCase() === 'tspan') {
        text += child.textContent || '';
      }
    }
  }
  
  return text.trim();
};

/**
 * 解析 SVG 内容，提取可编辑的子元素
 */
export const parseSvgContent = (svgContent: string): SvgSubElement[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`,
    'image/svg+xml'
  );
  
  const svgRoot = doc.documentElement;
  const result: SvgSubElement[] = [];
  
  const parseElement = (element: Element): SvgSubElement | null => {
    const tagName = element.tagName.toLowerCase();
    
    // 跳过定义类和动画类标签
    if (SKIP_TAGS.includes(tagName)) {
      return null;
    }
    
    if (!EDITABLE_TAGS.includes(tagName)) {
      return null;
    }
    
    // 收集原始属性
    const originalAttributes: Record<string, string> = {};
    for (const attr of Array.from(element.attributes)) {
      originalAttributes[attr.name] = attr.value;
    }
    
    // 生成或使用现有 ID
    const subId = originalAttributes['id'] || generateSubElementId();

    // ========== 新增：提取原始颜色值 ==========
    const fillValue = originalAttributes['fill'] || null;
    const strokeValue = originalAttributes['stroke'] || null;
    const strokeWidthValue = originalAttributes['stroke-width'];
    const opacityValue = originalAttributes['opacity'];
    
    const originalFill = fillValue || undefined;
    const originalStroke = strokeValue || undefined;
    const originalStrokeWidth = strokeWidthValue ? parseFloat(strokeWidthValue) : undefined;
    const originalOpacity = opacityValue ? parseFloat(opacityValue) : undefined;
    
    const isFillGradient = isGradientReference(fillValue);
    const isStrokeGradient = isGradientReference(strokeValue);
    
    // ========== 新增：提取文本内容（仅 text 元素） ==========
    let originalTextContent: string | undefined;
    if (tagName === 'text') {
      originalTextContent = extractTextContent(element);
    }
    
    // 解析子元素（针对 g 标签）
    const children: SvgSubElement[] = [];
    if (tagName === 'g') {
      for (const child of Array.from(element.children)) {
        const parsed = parseElement(child);
        if (parsed) {
          children.push(parsed);
        }
      }
    }
    
    return {
      id: subId,
      tagName: tagName as SvgSubElement['tagName'],
      originalAttributes,
      customAttributes: {},
      children: children.length > 0 ? children : undefined,
      rawContent: element.outerHTML,
      // 新增字段
      originalFill,
      originalStroke,
      originalStrokeWidth,
      originalOpacity,
      originalTextContent,
      isFillGradient,
      isStrokeGradient,
    };
  };
  
  // 递归遍历所有子元素
  const traverseAndParse = (element: Element) => {
    for (const child of Array.from(element.children)) {
      const tagName = child.tagName.toLowerCase();
      
      // 跳过定义类标签
      if (SKIP_TAGS.includes(tagName)) {
        continue;
      }
      
      const parsed = parseElement(child);
      if (parsed) {
        result.push(parsed);
      } else {
        // 如果当前元素不可编辑，继续遍历其子元素
        traverseAndParse(child);
      }
    }
  };
  
  traverseAndParse(svgRoot);
  
  return result;
};

/**
 * 根据 subElements 和自定义属性，重新生成 SVG 内容
 * 
 * 工作原理：
 * 1. 解析原始 SVG 内容
 * 2. 对于每个可编辑元素，检查是否有 customAttributes
 * 3. 如果有自定义值，使用自定义值覆盖
 * 4. 如果没有自定义值（undefined），保留原始属性值（不修改）
 */
export const rebuildSvgContent = (
  originalContent: string,
  subElements: SvgSubElement[]
): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${originalContent}</svg>`,
    'image/svg+xml'
  );
  
  // 构建 subElement id -> subElement 的映射
  const subElementMap = new Map<string, SvgSubElement>();
  const buildMap = (elements: SvgSubElement[]) => {
    elements.forEach(el => {
      subElementMap.set(el.id, el);
      if (el.children) {
        buildMap(el.children);
      }
    });
  };
  buildMap(subElements);
  
  // 扁平化所有子元素，用于按顺序匹配
  const allSubElements: SvgSubElement[] = [];
  const flattenSubElements = (elements: SvgSubElement[]) => {
    elements.forEach(el => {
      allSubElements.push(el);
      if (el.children) {
        flattenSubElements(el.children);
      }
    });
  };
  flattenSubElements(subElements);
  
  let subIndex = 0;
  
  // 递归应用自定义属性
  const applyAttributes = (element: Element) => {
    const tagName = element.tagName.toLowerCase();
    
    // 跳过定义类标签
    if (SKIP_TAGS.includes(tagName)) {
      return;
    }
    
    if (EDITABLE_TAGS.includes(tagName)) {
      // 尝试通过 ID 匹配
      const elementId = element.getAttribute('id');
      let subEl = elementId ? subElementMap.get(elementId) : undefined;
      
      // 如果没有匹配到，按顺序分配
      if (!subEl && subIndex < allSubElements.length) {
        subEl = allSubElements[subIndex];
        subIndex++;
        
        // 设置 ID 以便后续匹配
        if (!elementId && subEl) {
          element.setAttribute('id', subEl.id);
        }
      }
      
      if (subEl) {
        const custom = subEl.customAttributes;
        
        // ========== 关键修改：处理填充色 ==========
        if (custom.fill !== undefined) {
          // 有自定义值，使用自定义值
          element.setAttribute('fill', custom.fill);
        }
        // 如果 custom.fill === undefined，不做任何操作，保留原始值
        // 原始 SVG 内容中的 fill 属性会被保留
        
        // ========== 处理描边色 ==========
        if (custom.stroke !== undefined) {
          element.setAttribute('stroke', custom.stroke);
        }
        
        // ========== 处理描边宽度 ==========
        if (custom.strokeWidth !== undefined) {
          element.setAttribute('stroke-width', String(custom.strokeWidth));
        }
        
        // ========== 处理透明度 ==========
        if (custom.opacity !== undefined) {
          element.setAttribute('opacity', String(custom.opacity));
        }
        
        // ========== 处理偏移 ==========
        if (custom.offsetX !== undefined || custom.offsetY !== undefined) {
          const offsetX = custom.offsetX ?? 0;
          const offsetY = custom.offsetY ?? 0;
          
          if (offsetX !== 0 || offsetY !== 0) {
            const existingTransform = element.getAttribute('transform') || '';
            
            // 检查是否已经有 translate
            if (existingTransform.includes('translate')) {
              // 替换现有的 translate
              const newTransform = existingTransform.replace(
                /translate\([^)]*\)/,
                `translate(${offsetX}, ${offsetY})`
              );
              element.setAttribute('transform', newTransform);
            } else {
              // 添加新的 translate
              element.setAttribute(
                'transform',
                `translate(${offsetX}, ${offsetY}) ${existingTransform}`.trim()
              );
            }
          }
        }
        
        // ========== 处理文本内容（仅 text 元素） ==========
        if (tagName === 'text' && custom.textContent !== undefined) {
          // 清除现有内容
          while (element.firstChild) {
            element.removeChild(element.firstChild);
          }
          // 设置新文本
          element.textContent = custom.textContent;
        }
      }
    }
    
    // 递归处理子元素
    Array.from(element.children).forEach(child => applyAttributes(child));
  };
  
  applyAttributes(doc.documentElement);
  
  return doc.documentElement.innerHTML;
};

/**
 * 为编辑模式准备 SVG 内容
 * - 递归为所有可编辑元素添加 data-sub-id 属性
 * - 为选中元素添加高亮样式
 */
export const prepareSvgForEditMode = (
  svgContent: string,
  subElements: SvgSubElement[],
  selectedSubId: string | null = null
): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`,
    'image/svg+xml'
  );
  
  // 构建 id 映射（扁平化所有子元素）
  const allSubElements: SvgSubElement[] = [];
  const flattenSubElements = (elements: SvgSubElement[]) => {
    elements.forEach(el => {
      allSubElements.push(el);
      if (el.children) {
        flattenSubElements(el.children);
      }
    });
  };
  flattenSubElements(subElements);
  
  // 用于按顺序分配 subElement
  let subIndex = 0;
  
  // 递归处理元素
  const processElement = (element: Element) => {
    const tagName = element.tagName.toLowerCase();
    
    // 跳过定义类和动画类标签
    if (SKIP_TAGS.includes(tagName)) {
      return;
    }
    
    if (EDITABLE_TAGS.includes(tagName)) {
      // 尝试获取或分配 ID
      let elementId = element.getAttribute('id');
      let matchedSub: SvgSubElement | undefined;
      
      // 优先通过 ID 匹配
      if (elementId) {
        matchedSub = allSubElements.find(s => s.id === elementId);
      }
      
      // 如果没有匹配到，按顺序分配
      if (!matchedSub && subIndex < allSubElements.length) {
        matchedSub = allSubElements[subIndex];
        subIndex++;
        
        // 设置 ID
        if (!elementId) {
          element.setAttribute('id', matchedSub.id);
        }
      }
      
      if (matchedSub) {
        // 添加 data-sub-id 用于点击识别
        element.setAttribute('data-sub-id', matchedSub.id);
        
        // 添加指针样式
        const existingStyle = element.getAttribute('style') || '';
        if (!existingStyle.includes('cursor')) {
          element.setAttribute('style', `${existingStyle}; cursor: pointer;`.replace(/^;\s*/, ''));
        }
        
        // 如果是选中元素，添加高亮效果
        if (selectedSubId === matchedSub.id) {
          // 保存原始值用于恢复
          const currentStroke = element.getAttribute('stroke');
          const currentStrokeWidth = element.getAttribute('stroke-width');
          
          if (currentStroke) {
            element.setAttribute('data-original-stroke', currentStroke);
          }
          if (currentStrokeWidth) {
            element.setAttribute('data-original-stroke-width', currentStrokeWidth);
          }
          
          // 应用高亮样式
          element.setAttribute('stroke', '#3b82f6');
          element.setAttribute('stroke-width', '3');
        }
      }
    }
    
    // 递归处理子元素
    Array.from(element.children).forEach(child => processElement(child));
  };
  
  processElement(doc.documentElement);
  
  return doc.documentElement.innerHTML;
};